import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  updateProfile,
  GoogleAuthProvider,
  OAuthProvider,
  signInWithPopup,
  fetchSignInMethodsForEmail,
  linkWithCredential,
} from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import React, { useState, useEffect, useRef } from "react";
import {
  firebaseAuth,
  firebaseDB,
  firebaseStorage,
  firebaseFunctions,
  COLLECTIONS,
} from "./firebaseApp";
import { doc, onSnapshot, getDoc } from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { ref, getDownloadURL } from "firebase/storage";
import { resizeAndUpload } from "./firebaseActions";
import { clearPerUserClientState } from "../utils/perUserClientState";

/* .... */
import { useDispatch, useSelector } from "react-redux";
import { userActions, loadUserInfoData } from "./user-slice";
import { refreshClaims } from "./refreshClaims";
import { grantSignalFromUserDoc } from "./grantSignal";
import { accessNoticesActions } from "./accessNoticesSlice";
import { cartActions } from "./cart-slice";

const AuthContext = React.createContext({
  user: null,
  userType: 0,
  isLoggedIn: false,
  loginWithEmail: (email, password) => {},
  singupWithEmail: (email, password) => {},
  signInWithGoogle: () => {},
  signInWithApple: () => {},
  logout: () => {},
  resestPassword: (email) => {},
});

export const AuthContextProvider = (props) => {
  const dispatch = useDispatch();
  const [currentUser, setCurrentUser] = useState(null);

  const userIsLoggedIn = !!currentUser;
  const [initLoading, setInitLoading] = useState(true);

  /* ---- [Auth and user managment] -----------*/
  const singupWithEmail = (email, password) => {
    return createUserWithEmailAndPassword(firebaseAuth, email, password);
  };

  const loginWithEmail = async (email, password) => {
    const userCredintial = await signInWithEmailAndPassword(firebaseAuth, email, password);

    return userCredintial;
  };

  const userLogOut = async () => {
    console.log("[AuthContext] Starting logout...");

    // Sign out of Firebase Auth (this clears all tokens and persistence)
    await signOut(firebaseAuth);

    clearPerUserClientState();

    console.log("[AuthContext] Firebase signOut complete");

    // Clear Redux state
    dispatch(cartActions.resetCart());
    dispatch(userActions.logOut());
    dispatch(accessNoticesActions.reset());

    console.log("[AuthContext] Redux state cleared, logout complete");
  };

  const resestPassword = async (email) => {
    const requestPasswordReset = httpsCallable(firebaseFunctions, "requestPasswordReset");
    const result = await requestPasswordReset({ email });
    return result.data;
  };

  /* ---- [Social Authentication Methods] -----------*/

  /**
   * Sign in with Google
   * Requests email and profile scopes only
   * Handles account linking if email already exists
   */
  const signInWithGoogle = async () => {
    const provider = new GoogleAuthProvider();
    // Request only email and profile information
    provider.addScope("email");
    provider.addScope("profile");

    try {
      const result = await signInWithPopup(firebaseAuth, provider);
      return result;
    } catch (error) {
      // Handle account exists with different credential
      if (error.code === "auth/account-exists-with-different-credential") {
        // Get the email of the user's account
        const email = error.customData?.email;

        // Get sign-in methods for this email
        const methods = await fetchSignInMethodsForEmail(firebaseAuth, email);

        // Throw a user-friendly error with details
        const methodsString = methods.includes("password")
          ? "email and password"
          : methods.join(", ");

        throw {
          code: "auth/account-exists-with-different-credential",
          email: email,
          existingMethods: methods,
          message: `An account with ${email} already exists using ${methodsString}. Please sign in with that method first.`,
        };
      }

      // Re-throw other errors
      throw error;
    }
  };

  /**
   * Sign in with Apple
   * Requests email and name scopes only
   * Handles account linking if email already exists
   */
  const signInWithApple = async () => {
    const provider = new OAuthProvider("apple.com");
    // Request only email and name information
    provider.addScope("email");
    provider.addScope("name");

    try {
      const result = await signInWithPopup(firebaseAuth, provider);
      return result;
    } catch (error) {
      // Handle account exists with different credential
      if (error.code === "auth/account-exists-with-different-credential") {
        // Get the email of the user's account
        const email = error.customData?.email;

        // Get sign-in methods for this email
        const methods = await fetchSignInMethodsForEmail(firebaseAuth, email);

        // Throw a user-friendly error with details
        const methodsString = methods.includes("password")
          ? "email and password"
          : methods.join(", ");

        throw {
          code: "auth/account-exists-with-different-credential",
          email: email,
          existingMethods: methods,
          message: `An account with ${email} already exists using ${methodsString}. Please sign in with that method first.`,
        };
      }

      // Re-throw other errors
      throw error;
    }
  };

  const updateCustomClaims = async () => {
    const auth = getAuth();
    return refreshClaims(auth.currentUser, dispatch);
  };

  // Resolves once saved and rejects on failure, so callers can say so.
  const updateUserProfile = async (fulleName, profilePicURL) =>
    updateProfile(currentUser, {
      displayName: fulleName,
      photoURL: profilePicURL,
    })
      .then(() => {
        const auth = getAuth();
        const user = auth.currentUser;
        updateCustomClaims().catch((error) => {
          console.warn("Claims refresh after profile update failed:", error?.code ?? "unknown");
        });
        setCurrentUser(user);
        dispatch(userActions.setUser(user));
      })
      .catch((error) => {
        console.error("Error updating user profile:", error?.code ?? "unknown");
        throw error;
      });

  /* ---- [Storage and file uploads]------- */
  const uploadUserProfilePic = async (file) => {
    // Resize before upload so the photo always lands under the Storage rule
    // limit — the customer never sees a size rejection on their profile photo.
    return resizeAndUpload(file, "profile", () => `users/${currentUser.uid}/ProfilePic`);
  };

  const getProfilePicURL = async (userUID) => {
    try {
      const userPicRef = ref(firebaseStorage, `users/${userUID}/ProfilePic`);
      const downloadURL = await getDownloadURL(userPicRef);
      return downloadURL;
    } catch (error) {
      // User doesn't have a profile picture - return null to use default avatar
      // This is expected behavior for users who haven't uploaded a picture
      if (error.code === "storage/object-not-found") {
        return null;
      }
      // For other errors, log and return null
      console.warn("Error loading profile picture:", error);
      return null;
    }
  };

  /* -----------[Use Effects]---------------------- */
  /* Firebase auth observer -------------- */
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(firebaseAuth, (user) => {
      console.log("Auth state changed:", user ? "User authenticated" : "No user", user);
      setCurrentUser(user);
      setInitLoading(false);
      if (user) {
        updateCustomClaims().catch((error) => {
          console.warn("Claims refresh on auth change failed:", error?.code ?? "unknown");
        });
      }
      dispatch(userActions.setUser(user));
    });

    return () => {
      unsubscribe();
    };
  }, [dispatch]);

  const unsubSnapRef = useRef(() => {});
  /* update user info  */
  useEffect(() => {
    const updateUserInfo = async () => {
      if (currentUser) {
        try {
          /* Load user data once instead of real-time listener */
          const userRef = doc(firebaseDB, COLLECTIONS.Users, currentUser.uid);

          // Load user data once
          const userDoc = await getDoc(userRef);
          if (userDoc.exists()) {
            dispatch(loadUserInfoData(currentUser.uid));
          }

          unsubSnapRef.current = onSnapshot(
            userRef,
            (doc) => {
              console.log("[user data snapshot]");
              // Straight off the snapshot, ahead of loadUserInfoData's second
              // read: a role change must reach the client on the first hop,
              // not on whichever of the two lands later.
              dispatch(userActions.setGrantSignal(grantSignalFromUserDoc(doc.data())));
              dispatch(loadUserInfoData(currentUser.uid));
            },
            (error) => {
              console.error("Error listening to user data:", error);
            }
          );
        } catch (error) {
          console.error("Error loading user data:", error);
        }
      } else {
        console.log("[UnSubscribe]: ");
        unsubSnapRef.current();
        unsubSnapRef.current = () => {};
      }
    };

    updateUserInfo();
  }, [currentUser, dispatch]);

  /* -------------------------------------- */
  const contexValue = {
    user: currentUser,
    isLoggedIn: userIsLoggedIn,
    loginWithEmail,
    singupWithEmail,
    signInWithGoogle,
    signInWithApple,
    userLogOut,
    updateCustomClaims,
    resestPassword,
    updateUserProfile,
    uploadUserProfilePic,
    getProfilePicURL,
  };

  return (
    <AuthContext.Provider value={contexValue}>
      {!initLoading && props.children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
